/**
 * Mapper `fila de /propiedades` → `Property` del calendario.
 *
 * Vive aparte (y no inline en `fetchProperties`) para poder probarlo con la
 * respuesta REAL del backend: este mapper es el que decide qué campos de la
 * propiedad llegan al modal de la reserva y al menú de la grilla. Todo campo
 * que esos componentes lean de `Property` tiene que pasar por acá — si no se
 * copia, el componente lo ve `undefined` sin ningún error (round 2026-10-28:
 * `mensaje_inicial` no se copiaba y «Copiar instrucciones» avisaba que la
 * propiedad no tenía mensaje aunque lo tuviera). Ver docs/modulo-calendario.md.
 */

import type { Property } from "./useCalendarioLogic";

function safeNumber(v: any): number | undefined {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * @param p fila cruda de `GET /propiedades` (snake_case, tal cual el modelo)
 * @param userIsAdmin admin/superadmin. Los campos que solo ve el admin
 *   (`mensajeInicial`) quedan en null para los demás roles.
 */
export function mapPropiedadCalendario(p: any, userIsAdmin: boolean): Property {
  return {
    id: p.id,
    nombre: p.nombrepropiedad,
    color: p.color ?? null,
    costoAseo: safeNumber(p.precio_limpieza_cobrado) ?? null,
    sueldoAseo: safeNumber(p.precio_limpieza_aseador) ?? null,
    tasaComisionAf: (safeNumber(p.comision_propiedad) ?? 0) / 100,
    idPropiedadCorto: p.id_propiedad_corto ?? null,
    precioMinimoNoche: safeNumber(p.precio_minimo_noche) ?? null,
    // El mapper NO puede descartar este campo: «Copiar instrucciones»
    // (modal de la reserva y menú de la grilla) lo lee de esta lista.
    // Solo admin: el cliente no tiene esos botones y no lo necesita.
    mensajeInicial: userIsAdmin ? (p.mensaje_inicial ?? null) : null,
    transporteNeto: safeNumber(p.precio_transporte_neto) ?? null,
    transporteTotal: safeNumber(p.precio_transporte_total) ?? null,
    materialesNeto: safeNumber(p.precio_materiales_neto) ?? null,
    materialesTotal: safeNumber(p.precio_materiales_total) ?? null,
    lavanderiaNeto: safeNumber(p.precio_lavanderia_neto) ?? null,
    lavanderiaTotal: safeNumber(p.precio_lavanderia_total) ?? null,
    // hasLavanderiaAf = true solo si la propiedad apunta a una
    // lavandería AF INTERNA (es_generica=false). Si apunta a la
    // "General Externa" (es_generica=true) o no tiene lavandería,
    // hasLavanderiaAf=false y el total lavandería suma al ingreso
    // AF Empresa. Doc flujo-financiero.md §7.
    hasLavanderiaAf: Boolean(p.esLavanderiaInterna),
    // Geografía + cliente para ordenar/filtrar y el subtexto de la fila.
    regionId: p.region ?? null,
    comunaId: p.comuna ?? null,
    sectorId: p.sector ?? null,
    regionNombre: p.regionNombre ?? null,
    comunaNombre: p.comunaNombre ?? null,
    sectorNombre: p.sectorNombre ?? null,
    clienteNombre: p.client?.displayName ?? null,
    propietarioNombres: Array.isArray(p.propietarioNombres) ? p.propietarioNombres : [],
  };
}
